/**
 * Detect the *original* job-board a listing came from — even when we fetched
 * it via an aggregator like Adzuna. We inspect (in this order):
 *   1. the `source` field the backend already normalised (e.g. "Adzuna", "LinkedIn")
 *   2. the redirect/apply URL — the host almost always leaks the origin
 *   3. fall back to "other"
 *
 * Consumers use this to render a small brand pill on each result card and in
 * the landing-page marketing block ("we search LinkedIn, Indeed, …").
 */

const HOST_MATCHERS = [
  { key: "linkedin", hosts: ["linkedin.com", "lnkd.in"], label: "LinkedIn", color: "#0A66C2" },
  { key: "indeed", hosts: ["indeed.com", "indeed.nl", "nl.indeed.com"], label: "Indeed", color: "#003A9B" },
  { key: "glassdoor", hosts: ["glassdoor.com", "glassdoor.nl"], label: "Glassdoor", color: "#0CAA41" },
  { key: "monster", hosts: ["monster.com", "monsterboard.nl"], label: "Monster", color: "#6E46AE" },
  { key: "ziprecruiter", hosts: ["ziprecruiter.com"], label: "ZipRecruiter", color: "#1969FF" },
  { key: "nationale", hosts: ["nationalevacaturebank.nl"], label: "Nationale Vacaturebank", color: "#E30613" },
  { key: "striive", hosts: ["striive.com", "app.striive.com"], label: "Striive", color: "#0E1837" },
  { key: "freelance", hosts: ["freelance.nl"], label: "Freelance.nl", color: "#F26C3A" },
  { key: "jobbird", hosts: ["jobbird.com", "jobbird.nl"], label: "Jobbird", color: "#F58220" },
  { key: "twago", hosts: ["twago.com", "twago.nl", "twago.de"], label: "Twago", color: "#00A1E0" },
  { key: "randstadfreelance", hosts: ["freelance.randstad.nl", "randstad.nl"], label: "Randstad Freelance", color: "#0033A0" },
  { key: "adzuna", hosts: ["adzuna.nl", "adzuna.com", "adzuna.be"], label: "Adzuna", color: "#2F80ED" },
];

const SOURCE_ALIASES = {
  linkedin: "linkedin", "linkedin jobs": "linkedin",
  indeed: "indeed",
  glassdoor: "glassdoor",
  monster: "monster",
  ziprecruiter: "ziprecruiter", "zip recruiter": "ziprecruiter",
  striive: "striive",
  "freelance.nl": "freelance", "freelance nl": "freelance",
  jobbird: "jobbird",
  twago: "twago",
  "randstad freelance": "randstadfreelance", randstad: "randstadfreelance",
  adzuna: "adzuna",
};

function parseHost(url) {
  if (!url) return "";
  try {
    return new URL(url).host.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * Returns { key, label, color, viaAdzuna } for a job listing.
 * `viaAdzuna=true` means the aggregator was Adzuna even though the true
 * origin is e.g. LinkedIn — used to render a subtle "via Adzuna" tag.
 */
export function detectJobSource(job) {
  const sourceName = (job?.source || "").trim().toLowerCase();
  const alias = SOURCE_ALIASES[sourceName];
  const host = parseHost(job?.url || job?.redirect_url);

  // 1. Direct match on job.source (e.g. JSearch returns "LinkedIn")
  if (alias) {
    const match = HOST_MATCHERS.find((m) => m.key === alias);
    if (match) return { ...match, viaAdzuna: false };
  }

  // 2. Match on redirect-url host — reveals the real board even when
  // Adzuna wrapped it.
  const hostMatch = HOST_MATCHERS.find((m) => m.hosts.some((h) => host.endsWith(h)));
  if (hostMatch) {
    const isAdzunaAggregator = sourceName === "adzuna" && hostMatch.key !== "adzuna";
    return { ...hostMatch, viaAdzuna: isAdzunaAggregator };
  }

  // 3. Adzuna without a resolvable host stays as Adzuna.
  if (sourceName === "adzuna") {
    return { key: "adzuna", label: "Adzuna", color: "#2F80ED", viaAdzuna: false };
  }

  return { key: "other", label: job?.source || "Overig", color: "#666", viaAdzuna: false };
}

/** Static list used by marketing surfaces (landing page, dashboard widget). */
export const PROMOTED_SOURCES = [
  { key: "linkedin", label: "LinkedIn", color: "#0A66C2" },
  { key: "indeed", label: "Indeed", color: "#003A9B" },
  { key: "glassdoor", label: "Glassdoor", color: "#0CAA41" },
  { key: "nationale", label: "Nationale Vacaturebank", color: "#E30613" },
  { key: "striive", label: "Striive", color: "#0E1837" },
  { key: "freelance", label: "Freelance.nl", color: "#F26C3A" },
  { key: "jobbird", label: "Jobbird", color: "#F58220" },
  { key: "twago", label: "Twago", color: "#00A1E0" },
  { key: "randstadfreelance", label: "Randstad Freelance", color: "#0033A0" },
  { key: "adzuna", label: "Adzuna", color: "#2F80ED" },
];
